import { useCallback, useEffect, useRef, type MouseEvent } from 'react'
import { RichEditHost, type RichEditChangeMeta, type RichEditFormat } from '@/app/richEdit'
import { classNames, legacyContractDataAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { contract, css } from './Editor.helpers'
import { useEditorFormModelSnapshot } from './EditorTemplateContract'
import { useEditorSession } from './EditorSessionContext'
import { useEditorSurface } from './EditorSurfaceContext'
import { useProductDisplayCatalog } from './ProductDisplayCatalogContext'
import { creationEditPinGuard } from '@/contracts/productCreation'

function legacyEnabled(value: unknown): boolean {
  return value === true || value === 1 || value === '1'
}

function handleToggleBar(event: MouseEvent<HTMLElement>, barId: number) {
  dispatchEditorAction({ action: 'toggle-rich-bar', target: event.currentTarget, barId })
}

export function EditorBodyFields() {
  const {
    variant,
    skin,
    richEditVariant,
    richEditPolicy,
    richEditSkin,
    richEditDefaultFormat,
  } = useEditorSurface()
  const editorSession = useEditorSession()
  const isMailbox = variant === 'mailbox'
  const model = useEditorFormModelSnapshot()
  const productCatalog = useProductDisplayCatalog()
  const isHtml = model?.fields.bodyIsHtml ?? false
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const initializedHtmlModes = useRef(new Set<string>())
  const standardProduct = productCatalog.find(product => product.path === 'std')
  const configuredProduct = productCatalog.find(product => product.comboValue === Number(model?.settings.is_view_combo))
  const selectedProduct = legacyEnabled(model?.settings.is_view) && configuredProduct
    ? configuredProduct
    : standardProduct
  const pinGuard = creationEditPinGuard(selectedProduct?.path, model?.pins.edit ?? {})
  const showPinWarning = Boolean(model && model.isUpdate !== true && pinGuard.isUnpinned)

  useEffect(() => {
    if (richEditDefaultFormat !== 'html' || isHtml || !model) return
    const key = `${model.mode}:${model.draftElId ?? 'new'}`
    if (initializedHtmlModes.current.has(key)) return
    initializedHtmlModes.current.add(key)
    const textarea = textareaRef.current
    if (!textarea) return
    const html = textarea.value.replace(/(?:\r\n|\r|\n)/g, '<br />')
    textarea.value = html
    dispatchEditorAction({ action: 'set-body-html', isHtml: true, value: html, reason: 'react-body:default-rich' })
  }, [isHtml, model, richEditDefaultFormat])

  const syncValue = useCallback((value: string, meta: RichEditChangeMeta) => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.value = value
    dispatchEditorAction({ action: 'sync-input', target: textarea, reason: `react-body:${meta.reason}` })
  }, [])

  const changeFormat = useCallback((format: RichEditFormat, value: string) => {
    const textarea = textareaRef.current
    if (textarea) textarea.value = value
    dispatchEditorAction({
      action: 'set-body-html',
      isHtml: format === 'html',
      value,
      reason: format === 'html' ? 'react-body:to-rich' : 'react-body:to-plain',
    })
  }, [])

  const compatibilityToolbar = (
    <span hidden aria-hidden="true" style={{ display: 'none' }}>
      <span {...legacyContractDataAttrs('editor.body.isDirty')} className={css('editor.body.toolButton', 'editor.extName.button')} />
      <span {...legacyContractDataAttrs('editor.body.richExt')} style={{ display: 'none' }} className={classNames(css('editor.body.richToolbar'), skin.body.legacyToolbarHidden)} hidden aria-hidden>
        <span onClick={event => handleToggleBar(event, 2)}>Styles</span>
        <span onClick={event => handleToggleBar(event, 3)}>More styles</span>
        <span onClick={event => handleToggleBar(event, 4)}>Templates</span>
        <span onClick={event => handleToggleBar(event, 5)}>Table</span>
        <span onClick={event => handleToggleBar(event, 6)}>Find</span>
        <span onClick={event => handleToggleBar(event, 7)}>Elements</span>
      </span>
      <span {...legacyContractDataAttrs('editor.body.dskEls')} style={{ display: 'none' }} className={classNames(css('editor.body.dskToolbar'), skin.body.legacyToolbarHidden)} hidden aria-hidden>
        <span {...{ attr_id: '1' }}>Link</span>
        <span {...{ attr_id: '2' }}>Video</span>
      </span>
    </span>
  )

  return (
    <div {...contract('editor.body')} className={classNames('md-editor__body', skin.body.root)}>
      <RichEditHost
        value={model?.fields.bodyText ?? ''}
        format={isHtml ? 'html' : 'plain'}
        variant={richEditVariant}
        policy={richEditPolicy}
        active={Boolean(editorSession.mode)}
        sessionKey={`${editorSession.mode}:${model?.draftElId ?? 'new'}`}
        skin={richEditSkin}
        canonicalRef={textareaRef}
        syncKey={model?.syncedAt}
        onChange={syncValue}
        onFormatChange={changeFormat}
        modeButtonProps={legacyContractDataAttrs('editor.body.richFormatButton')}
        toolbarAfter={compatibilityToolbar}
        plainControl={(
          <textarea
            {...contract('editor.body.textarea')}
            ref={textareaRef}
            className={skin.body.textarea}
            name="msg_text"
            placeholder={isMailbox ? 'Write your message' : undefined}
            wrap="virtual"
            hidden={isHtml}
            onInput={event => dispatchEditorAction({ action: 'sync-input', target: event.currentTarget, reason: 'react-body:input' })}
          />
        )}
      />
      {showPinWarning ? (
        <p
          data-md-contract="creation.edit-pin-warning"
          role={pinGuard.blocksSave ? 'alert' : 'status'}
          className={classNames('md-editor__edit-pin-warning', skin.body.editPinWarning)}
        >
          {pinGuard.message}
        </p>
      ) : null}
    </div>
  )
}
